<template>
  <ExpandedSideSection
    :loading="roleStore?.isLoadingSelected"
    :no-data="!selectedRole"
    :back-route="isPublicContext ? { name: 'Public Jobs', query: { ...route.query } } : { name: 'All jobs', query: { ...route.query } }"
    :edit-route="(isGuide || isFellow) ? { name: 'Role modal edit', params: { role_id: String(route.params.role_id) }, query: { ...route.query } } : undefined"
  >
    <div class="relative rounded-xl border border-gray-200 bg-white p-4">
      <JobMainData :role-id="String(route.params.role_id)">
        <!-- Applicants — hidden for limited/public users -->
        <template v-if="!isLimitedUser && !isPublicContext" #fields="{ rowClass, labelClass }">
          <div :class="rowClass">
            <dt :class="labelClass"><Users class="h-3.5 w-3.5" />Applicants</dt>
            <dd>
              <ul v-if="selectedRole?.role_applications?.length" class="flex flex-wrap gap-1.5">
                <li
                  v-for="application in selectedRole?.role_applications"
                  :key="application.application_id"
                  class="rounded-full border border-gray-200 px-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100"
                >
                  <router-link
                    v-if="!isBlurred"
                    :to="{
                      path: `/jobs/${selectedRole?.id}/${application.application_id}`,
                      query: { ...route.query }
                    }"
                    >{{ application?.first_name }} {{ application?.last_name }}</router-link
                  >
                  <span v-else :class="{ 'privacy-blur': isBlurred }">
                    {{ application?.first_name }} {{ application?.last_name }}
                  </span>
                </li>
              </ul>
              <span v-else class="text-gray-400">No applicants yet</span>
            </dd>
          </div>
        </template>
      </JobMainData>

      <section v-if="selectedRole?.qualifications" class="mt-5">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Qualifications</h3>
        <p class="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
          {{ selectedRole.qualifications }}
        </p>
      </section>

      <section v-if="selectedRole?.description" class="mt-5">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">About the role</h3>
        <p class="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
          {{ selectedRole.description }}
        </p>
      </section>

      <p
        v-if="selectedRole?.is_scraped"
        class="mt-5 flex items-start gap-2 rounded-lg bg-cosmicPurple/5 p-3 text-xs leading-relaxed text-gray-600"
      >
        <Sparkles class="mt-0.5 h-3.5 w-3.5 shrink-0 text-cosmicPurple" />
        Our job finder discovered this role on the company's careers page. Details may have
        changed, so please verify them on the original posting.
      </p>

      <!-- Primary action, pinned to the bottom of the panel -->
      <div
        class="md:sticky -bottom-2 -mx-4 -mb-4 mt-5 flex justify-end rounded-b-xl border-t border-gray-100 bg-white px-4 py-3"
      >
        <button
          v-if="addApplicationButton !== 'See Your Application'"
          class="rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors duration-150"
          :class="
            isAddDisabled
              ? 'cursor-not-allowed bg-gray-300'
              : 'bg-cosmicPurple hover:bg-cosmicPurple/90'
          "
          @click="createApplication"
          :disabled="isAddDisabled"
        >
          {{ addApplicationButton }}
        </button>
        <router-link
          v-else
          :to="{ path: applicationPath }"
          class="rounded-lg bg-cosmicPurple px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-cosmicPurple/90"
        >
          {{ addApplicationButton }}
        </router-link>
      </div>
    </div>
  </ExpandedSideSection>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Users, Sparkles } from 'lucide-vue-next'
import { useRolesStore } from '@/stores/roles'
import { useApplicationsStore } from '@/stores/applications'
import { useUserStore } from '@/stores/user'
import JobMainData from '@/components/common/JobMainData.vue'
import { useAuth } from '@/composables/useAuth'
import { usePrivacy } from '@/composables/usePrivacy'
import type { OpenRole } from '@/types/openRole'
import { storeToRefs } from 'pinia'
import { toast } from '@/components/ui/toast'
import ExpandedSideSection from '@/components/common/ExpandedSideSection.vue'

const { isGuide, isFellow, isLimitedUser } = useAuth()
const { isBlurred } = usePrivacy()

const roleStore = useRolesStore()
const userStore = useUserStore()
const authStore = useAuthStore()

const route = useRoute()
const router = useRouter()

const isPublicContext = computed(() => route.path.startsWith('/explore/'))

const { selectedRole: selectedRoleRef } = storeToRefs(roleStore)
const selectedRole = computed<OpenRole | null>(() => selectedRoleRef.value)

const applicationsStore = useApplicationsStore()

const hasUserApplied = computed(() => {
  const id = selectedRole.value?.my_application_id
  if (id != null) return true
  return !!selectedRole.value?.role_applications?.some(
    (application: { user_id: number }) => application.user_id === userStore.currentUser?.id
  )
})

const userApplication = computed(() => {
  const myId = selectedRole.value?.my_application_id
  const uid = userStore.currentUser?.id
  if (myId != null && uid != null) {
    return { application_id: myId, user_id: uid }
  }
  return selectedRole.value?.role_applications?.find(
    (app: { user_id: number; application_id: number }) => app.user_id === uid
  )
})

const addApplicationState = ref<'idle' | 'loading' | 'success'>('idle')
const userHasApplied = ref(false)
const newApplicationId = ref<number | null>(null)

const addApplicationButton = computed(() => {
  if (hasUserApplied.value || userHasApplied.value) {
    return 'See Your Application'
  }
  if (addApplicationState.value === 'loading') return 'Adding...'
  if (addApplicationState.value === 'success') return 'Added!'
  return '+ Add to your applications'
})

const isAddDisabled = computed(() => {
  if (!selectedRole.value?.status_open) return true
  return addApplicationState.value !== 'idle'
})

const applicationPath = computed(() => {
  const applicationId =
    newApplicationId.value ||
    selectedRole.value?.my_application_id ||
    userApplication.value?.application_id
  return `/applications/${applicationId}`
})

watch(
  () => route.params.role_id,
  async (newRoleId) => {
    if (newRoleId) {
      // Reset states when role changes
      addApplicationState.value = 'idle'
      userHasApplied.value = false
      newApplicationId.value = null
      // Clear current role to avoid stale content before fetch completes
      selectedRoleRef.value = null
      await roleStore.fetchRoleById(newRoleId as string)
      // Scroll container to top on role change
      const container = document.querySelector(
        '.w-full.max-h-full.overflow-y-scroll'
      ) as HTMLElement | null
      if (container) container.scrollTop = 0
    }
  },
  { immediate: true }
)

async function createApplication() {
  if (!authStore.isAuthenticated) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/register')
    return
  }

  // Ensure we use the latest route role id
  const currentRoleId = Number(route.params.role_id)

  if (addApplicationButton.value === 'See Your Application' && userApplication.value) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(`/applications/${userApplication.value.application_id}`)
    return
  }

  addApplicationState.value = 'loading'
  try {
    const response = await applicationsStore.createApplication({
      open_role: currentRoleId
    })
    
    // Store the new application ID and mark that user has applied
    newApplicationId.value = response.id
    userHasApplied.value = true
    
    // Update the role to reflect the new application
    await roleStore.fetchRoleById(currentRoleId.toString())
    
    addApplicationState.value = 'success'

    // Show success toast
    toast({
      title: 'Success',
      description: `"${selectedRole?.value?.role_name}" has been added to your applications`
    })
  } catch (error) {
    // Show error toast
    toast({
      variant: 'destructive',
      title: 'Error',
      description: 'Failed to submit application'
    })
  } finally {
    addApplicationState.value = 'idle'
  }
}

onUnmounted(() => {
  // Clear states when component is destroyed
  addApplicationState.value = 'idle'
})
</script>

<style scoped>
.transition-opacity {
  transition-property: opacity;
}
</style>
